import React from "react";
import { useToast } from "@chakra-ui/react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { validateDomain, validateEmail } from "data/mockData";
import { createTenant, createTenantUser, fetchTenantReferralSources } from "action/tenantAction";
import { cloneTenantData } from "action/menuAction";
import { fetchPackages, transformPackagesData } from "action/packageAction";
import PackageSelectList from "components/PackageSelectList";

/* ── Icons ── */
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);
const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const STEP_LABELS = ["Tenant Info", "PIC Details", "Contacts", "Package", "Default User"];

function TenantCreateStep() {
  const { t } = useTranslation();
  const STEPS = STEP_LABELS.map((label, i) => ({ num: i + 1, label: t(label) }));

  React.useEffect(() => { document.title = t("Tenant Wizard") + " | LMS Tenant"; }, []);

  const toast = useToast();
  const history = useHistory();
  const query = useQuery();
  const mode = query.get("mode") || "new";

  const [step, setStep] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [packages, setPackages] = React.useState([]);
  const [packagesLoading, setPackagesLoading] = React.useState(false);
  const [referralSources, setReferralSources] = React.useState([]);
  const [referralSourcesLoading, setReferralSourcesLoading] = React.useState(false);

  const [tenantInfo, setTenantInfo] = React.useState({
    namaTenant: "", domainUnik: "", alamat: "", status: "Active", remark: "",
  });
  const [tenantInfoErrors, setTenantInfoErrors] = React.useState({});

  const [pics, setPics] = React.useState([{ nama: "", nomorTelepon: "", email: "" }]);
  const [picErrors, setPicErrors] = React.useState([]);

  const [contacts, setContacts] = React.useState([{ name: "", email: "", phone: "" }]);
  const [contactErrors, setContactErrors] = React.useState([]);
  const [referralSource, setReferralSource] = React.useState("");
  const [referralSourceError, setReferralSourceError] = React.useState("");

  const [subscription, setSubscription] = React.useState({
    packageId: null,
    customPackageName: "",
    customPackagePrice: 0,
    installationFee: 0,
    discountPercent: 0,
    tanggalMulai: "",
    tanggalBerakhir: "",
    gracePeriod: 0,
    jumlahStudent: 1,
    additionalAttachment: null,
  });
  const [subscriptionErrors, setSubscriptionErrors] = React.useState({});

  const [defaultUser, setDefaultUser] = React.useState({ email: "", password: "" });
  const [defaultUserErrors, setDefaultUserErrors] = React.useState({});
  const [showPassword, setShowPassword] = React.useState(false);

  const selectedPackage = React.useMemo(() => {
    if (subscription.packageId === null) {
      return { id: null, name: subscription.customPackageName || "Custom Package", price: subscription.customPackagePrice, benefits: [] };
    }
    return packages.find((p) => p.id === subscription.packageId);
  }, [subscription.packageId, subscription.customPackageName, subscription.customPackagePrice, packages]);

  const basePrice = React.useMemo(() => selectedPackage?.price || 0, [selectedPackage]);

  const durationMonths = React.useMemo(() => {
    if (!subscription.tanggalMulai || !subscription.tanggalBerakhir) return 1;
    const start = new Date(subscription.tanggalMulai);
    const end = new Date(subscription.tanggalBerakhir);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    return Math.max(months, 1);
  }, [subscription.tanggalMulai, subscription.tanggalBerakhir]);

  const discountedPricePerMonth = React.useMemo(() => {
    const discount = Math.min(Math.max(Number(subscription.discountPercent) || 0, 0), 100);
    return Math.round(basePrice * (1 - discount / 100));
  }, [basePrice, subscription.discountPercent]);

  const jumlahStudent = React.useMemo(() => Math.max(Number(subscription.jumlahStudent) || 1, 1), [subscription.jumlahStudent]);

  const discountedPrice = React.useMemo(() => discountedPricePerMonth * jumlahStudent * durationMonths, [discountedPricePerMonth, jumlahStudent, durationMonths]);

  const totalPrice = React.useMemo(() => {
    const install = Number(subscription.installationFee) || 0;
    return discountedPrice + install;
  }, [discountedPrice, subscription.installationFee]);

  React.useEffect(() => {
    const loadPackages = async () => {
      setPackagesLoading(true);
      try {
        const result = await fetchPackages({ per_page: 50 });
        if (result.success) {
          setPackages(transformPackagesData(result.data));
        } else {
          toast({ title: t("Error"), description: result.message || t("Failed to load packages"), status: "error", duration: 3000, isClosable: true });
        }
      } catch {
        toast({ title: t("Error"), description: t("An error occurred while loading packages"), status: "error", duration: 3000, isClosable: true });
      } finally {
        setPackagesLoading(false);
      }
    };

    const loadReferralSources = async () => {
      setReferralSourcesLoading(true);
      try {
        const result = await fetchTenantReferralSources();
        if (result.success) {
          setReferralSources(result.data);
        } else {
          toast({ title: t("Error"), description: result.message || t("Failed to load referral sources"), status: "error", duration: 3000, isClosable: true });
        }
      } catch {
        toast({ title: t("Error"), description: t("An error occurred while loading referral sources"), status: "error", duration: 3000, isClosable: true });
      } finally {
        setReferralSourcesLoading(false);
      }
    };

    loadPackages();
    loadReferralSources();
  }, [toast]);

  const validateStep1 = () => {
    const errs = {};
    if (!tenantInfo.namaTenant.trim()) errs.namaTenant = t("Nama tenant wajib");
    if (!tenantInfo.domainUnik.trim()) errs.domainUnik = t("Domain wajib");
    else if (!validateDomain(tenantInfo.domainUnik)) errs.domainUnik = t("Format domain tidak valid");
    if (!tenantInfo.alamat.trim()) errs.alamat = t("Alamat wajib");
    setTenantInfoErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs = pics.map(() => ({ nama: "", nomorTelepon: "", email: "" }));
    let ok = true;
    pics.forEach((p, i) => {
      if (!p.nama.trim()) { errs[i].nama = t("Nama wajib"); ok = false; }
      if (!p.nomorTelepon.trim()) { errs[i].nomorTelepon = t("Nomor telp wajib"); ok = false; }
      if (!p.email.trim()) { errs[i].email = t("Email wajib"); ok = false; }
      else if (!validateEmail(p.email)) { errs[i].email = t("Email tidak valid"); ok = false; }
    });
    setPicErrors(errs);
    return ok;
  };

  const validatePackageStep = () => {
    const errs = {};
    if (subscription.packageId === null) {
      if (!subscription.customPackageName.trim()) errs.customPackageName = t("Nama custom package wajib");
      if (subscription.customPackagePrice <= 0) errs.customPackagePrice = t("Harga custom package harus > 0");
    }
    if (!subscription.tanggalMulai) errs.tanggalMulai = t("Tanggal mulai wajib");
    if (!subscription.tanggalBerakhir) errs.tanggalBerakhir = t("Tanggal berakhir wajib");
    if (subscription.tanggalMulai && subscription.tanggalBerakhir) {
      if (new Date(subscription.tanggalMulai) >= new Date(subscription.tanggalBerakhir))
        errs.tanggalBerakhir = t("Tanggal berakhir harus setelah mulai");
    }
    if (subscription.discountPercent < 0 || subscription.discountPercent > 100)
      errs.discountPercent = t("Diskon 0-100%");
    if (subscription.gracePeriod < 0) errs.gracePeriod = t("Tidak boleh negatif");
    setSubscriptionErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateContactsStep = () => {
    const errs = contacts.map(() => ({ name: "", email: "", phone: "" }));
    let ok = true;
    contacts.forEach((c, i) => {
      if (!c.name.trim()) { errs[i].name = t("Nama wajib"); ok = false; }
      if (!c.email.trim()) { errs[i].email = t("Email wajib"); ok = false; }
      else if (!validateEmail(c.email)) { errs[i].email = t("Email tidak valid"); ok = false; }
      if (!c.phone.trim()) { errs[i].phone = t("Nomor telp wajib"); ok = false; }
    });
    setContactErrors(errs);
    if (!referralSource) { setReferralSourceError(t("Referral source wajib")); ok = false; }
    else setReferralSourceError("");
    return ok;
  };

  const validateDefaultUserStep = () => {
    const errs = {};
    if (!defaultUser.email.trim()) errs.email = t("Email wajib");
    else if (!validateEmail(defaultUser.email)) errs.email = t("Email tidak valid");
    if (!defaultUser.password.trim()) errs.password = t("Password wajib");
    else if (defaultUser.password.length < 6) errs.password = t("Password minimal 6 karakter");
    setDefaultUserErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateContactsStep()) return;
    if (step === 4 && !validatePackageStep()) return;
    setStep((prev) => Math.min(5, prev + 1));
  };
  const back = () => setStep((prev) => Math.max(1, prev - 1));

  const save = async () => {
    if (!validateDefaultUserStep()) return;
    setLoading(true);
    try {
      const tenantData = {
        name: tenantInfo.namaTenant,
        domain: tenantInfo.domainUnik,
        address: tenantInfo.alamat,
        status: tenantInfo.status.toLowerCase(),
        remark: tenantInfo.remark || null,
        id_referral_source: referralSource,
        subscription_start_at: subscription.tanggalMulai,
        subscription_expired_at: subscription.tanggalBerakhir,
        grace_period_days: subscription.gracePeriod,
        additional_attachment: subscription.additionalAttachment,
        id_package: subscription.packageId === null ? 0 : subscription.packageId,
        harga_package: basePrice,
        diskon: subscription.discountPercent,
        implementation_cost: subscription.installationFee,
        jumlah_student: jumlahStudent,
        total: totalPrice,
        pics: pics.map((pic) => ({ name: pic.nama, email: pic.email, phone: pic.nomorTelepon })),
        user_invitation: contacts
          .filter((c) => c.name && c.email && c.phone)
          .map((c) => ({ name: c.name, email: c.email, phone: c.phone })),
      };

      const result = await createTenant(tenantData);
      if (result.success) {
        const newTenantId = result.data?.id;
        if (newTenantId) {
          const cloneResult = await cloneTenantData(newTenantId);
          if (!cloneResult.success) {
            toast({ title: t("Warning"), description: cloneResult.message || t("Tenant created but cloning data failed"), status: "warning", duration: 4000, isClosable: true });
          }
          const userResult = await createTenantUser({ email: defaultUser.email, password: defaultUser.password, tenant_id: newTenantId });
          if (!userResult.success) {
            toast({ title: t("Warning"), description: userResult.message || t("Tenant created but default user creation failed"), status: "warning", duration: 4000, isClosable: true });
          }
        }
        toast({ title: t("Success"), description: result.message || t("Tenant created successfully"), status: "success", duration: 3000, isClosable: true });
        if (newTenantId) {
          history.push(`/admin/tenant-menu-setting/${newTenantId}`);
        } else {
          history.push("/admin/master-tenant");
        }
      } else {
        toast({ title: t("Error"), description: result.message || t("Failed to create tenant"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("An error occurred while creating tenant"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionChange = (field, value) => {
    setSubscription((prev) => ({ ...prev, [field]: value }));
    if (subscriptionErrors[field]) setSubscriptionErrors((prev) => ({ ...prev, [field]: "" }));
  };

  return (
    <div className="lms-page lms-anim-fade">
      {/* Header */}
      <div className="lms-page-header">
        <div className="lms-page-header-text">
          <div className="lms-breadcrumb">
            <span>{t("Apps")}</span><ChevronRightIcon /><span>{t("Admin")}</span><ChevronRightIcon />
            <span>{t("Master Tenant")}</span><ChevronRightIcon />
            <span className="lms-breadcrumb-current">{mode === "edit" ? t("Edit Tenant") : t("Create Tenant")}</span>
          </div>
          <div className="t-display">{mode === "edit" ? t("Edit Tenant") : t("Create New Tenant")}</div>
          <div className="t-small">{t("Isi formulir berikut secara bertahap untuk membuat tenant baru.")}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="lms-btn lms-btn-secondary" onClick={() => history.goBack()}>
            <ArrowLeftIcon /> {t("Back")}
          </button>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="lms-card" style={{ marginBottom: 20 }}>
        <div className="lms-card-body" style={{ padding: "20px 24px" }}>
          <div className="lms-wizard-stepper">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`lms-stepper-step ${step > s.num ? "done" : step === s.num ? "active" : ""}`}>
                  <div className="lms-stepper-num">
                    {step > s.num ? <CheckIcon /> : s.num}
                  </div>
                  <span className="lms-stepper-label">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className="lms-stepper-bar" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="lms-card">
        <div className="lms-card-body">

          {/* Step 1: Tenant Information */}
          {step === 1 && (
            <div>
              <div className="t-h2" style={{ marginBottom: 20 }}>{t("Tenant Information")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div className={`lms-form-group ${tenantInfoErrors.namaTenant ? "has-error" : ""}`}>
                  <label className="lms-label">{t("Nama Tenant")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                  <input
                    className="lms-input"
                    value={tenantInfo.namaTenant}
                    onChange={(e) => setTenantInfo({ ...tenantInfo, namaTenant: e.target.value })}
                    placeholder={t("Masukkan nama tenant")}
                  />
                  {tenantInfoErrors.namaTenant && <div className="lms-error-msg">{tenantInfoErrors.namaTenant}</div>}
                </div>
                <div className={`lms-form-group ${tenantInfoErrors.domainUnik ? "has-error" : ""}`}>
                  <label className="lms-label">{t("Domain")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                  <input
                    className="lms-input"
                    value={tenantInfo.domainUnik}
                    onChange={(e) => setTenantInfo({ ...tenantInfo, domainUnik: e.target.value })}
                    placeholder={t("example.com")}
                  />
                  {tenantInfoErrors.domainUnik && <div className="lms-error-msg">{tenantInfoErrors.domainUnik}</div>}
                </div>
              </div>

              <div className={`lms-form-group ${tenantInfoErrors.alamat ? "has-error" : ""}`} style={{ marginBottom: 16 }}>
                <label className="lms-label">{t("Alamat")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <textarea
                  className="lms-textarea"
                  rows={3}
                  value={tenantInfo.alamat}
                  onChange={(e) => setTenantInfo({ ...tenantInfo, alamat: e.target.value })}
                  placeholder={t("Masukkan alamat tenant")}
                />
                {tenantInfoErrors.alamat && <div className="lms-error-msg">{tenantInfoErrors.alamat}</div>}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div className="lms-form-group">
                  <label className="lms-label">{t("Status")}</label>
                  <select
                    className="lms-select"
                    value={tenantInfo.status}
                    onChange={(e) => setTenantInfo({ ...tenantInfo, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Non Active">Non Active</option>
                  </select>
                </div>
              </div>

              <div className="lms-form-group">
                <label className="lms-label">{t("Remark")} <span className="t-small lms-text-muted">{t("(opsional)")}</span></label>
                <textarea
                  className="lms-textarea"
                  rows={3}
                  value={tenantInfo.remark}
                  onChange={(e) => setTenantInfo({ ...tenantInfo, remark: e.target.value })}
                  placeholder={t("Masukkan remark (opsional)")}
                />
              </div>
            </div>
          )}

          {/* Step 2: PIC Details */}
          {step === 2 && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div className="t-h2">{t("Person In Charge (PIC)")}</div>
                <button
                  className="lms-btn lms-btn-secondary lms-btn-sm"
                  onClick={() => setPics([...pics, { nama: "", nomorTelepon: "", email: "" }])}
                >
                  <PlusIcon /> {t("Tambah PIC")}
                </button>
              </div>

              {pics.map((pic, idx) => (
                <div key={idx} className="lms-repeater-card">
                  <div className="lms-repeater-card-header">
                    <span className="t-h3">PIC #{idx + 1}</span>
                    {pics.length > 1 && (
                      <button
                        className="lms-btn lms-btn-ghost lms-btn-sm lms-btn-icon"
                        style={{ color: "var(--danger-500)" }}
                        onClick={() => {
                          setPics(pics.filter((_, i) => i !== idx));
                          setPicErrors(picErrors.filter((_, i) => i !== idx));
                        }}
                        title="Remove PIC"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>

                  <div className={`lms-form-group ${picErrors[idx]?.nama ? "has-error" : ""}`} style={{ marginBottom: 12 }}>
                    <label className="lms-label">{t("Nama PIC")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                    <input
                      className="lms-input"
                      value={pic.nama}
                      onChange={(e) => { const next = [...pics]; next[idx].nama = e.target.value; setPics(next); }}
                      placeholder={t("Masukkan nama PIC")}
                    />
                    {picErrors[idx]?.nama && <div className="lms-error-msg">{picErrors[idx].nama}</div>}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className={`lms-form-group ${picErrors[idx]?.nomorTelepon ? "has-error" : ""}`}>
                      <label className="lms-label">{t("Nomor Telepon")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input
                        className="lms-input"
                        maxLength={13}
                        value={pic.nomorTelepon}
                        onChange={(e) => { const next = [...pics]; next[idx].nomorTelepon = e.target.value; setPics(next); }}
                        placeholder={t("Masukkan nomor telepon")}
                      />
                      {picErrors[idx]?.nomorTelepon && <div className="lms-error-msg">{picErrors[idx].nomorTelepon}</div>}
                    </div>
                    <div className={`lms-form-group ${picErrors[idx]?.email ? "has-error" : ""}`}>
                      <label className="lms-label">{t("Email")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input
                        className="lms-input"
                        type="email"
                        value={pic.email}
                        onChange={(e) => { const next = [...pics]; next[idx].email = e.target.value; setPics(next); }}
                        placeholder={t("Masukkan email PIC")}
                      />
                      {picErrors[idx]?.email && <div className="lms-error-msg">{picErrors[idx].email}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 3: Contacts & Referral */}
          {step === 3 && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div className="t-h2">{t("Contacts & Referral Source")}</div>
                <button
                  className="lms-btn lms-btn-secondary lms-btn-sm"
                  onClick={() => setContacts([...contacts, { name: "", email: "", phone: "" }])}
                >
                  <PlusIcon /> {t("Tambah Contact")}
                </button>
              </div>

              <div className={`lms-form-group ${referralSourceError ? "has-error" : ""}`} style={{ marginBottom: 20 }}>
                <label className="lms-label">{t("Referral Source")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <select
                  className="lms-select"
                  value={referralSource}
                  disabled={referralSourcesLoading}
                  onChange={(e) => { setReferralSource(e.target.value); if (referralSourceError) setReferralSourceError(""); }}
                >
                  <option value="">{t("Pilih referral source")}</option>
                  {referralSources.map((source) => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </select>
                {referralSourceError && <div className="lms-error-msg">{referralSourceError}</div>}
              </div>

              {contacts.map((contact, idx) => (
                <div key={idx} className="lms-repeater-card">
                  <div className="lms-repeater-card-header">
                    <span className="t-h3">Contact #{idx + 1}</span>
                    {contacts.length > 1 && (
                      <button
                        className="lms-btn lms-btn-ghost lms-btn-sm lms-btn-icon"
                        style={{ color: "var(--danger-500)" }}
                        onClick={() => {
                          setContacts(contacts.filter((_, i) => i !== idx));
                          setContactErrors(contactErrors.filter((_, i) => i !== idx));
                        }}
                        title="Remove Contact"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>

                  <div className={`lms-form-group ${contactErrors[idx]?.name ? "has-error" : ""}`} style={{ marginBottom: 12 }}>
                    <label className="lms-label">{t("Nama")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                    <input
                      className="lms-input"
                      value={contact.name}
                      onChange={(e) => { const next = [...contacts]; next[idx].name = e.target.value; setContacts(next); }}
                      placeholder={t("Masukkan nama")}
                    />
                    {contactErrors[idx]?.name && <div className="lms-error-msg">{contactErrors[idx].name}</div>}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className={`lms-form-group ${contactErrors[idx]?.email ? "has-error" : ""}`}>
                      <label className="lms-label">{t("Email")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input
                        className="lms-input"
                        type="email"
                        value={contact.email}
                        onChange={(e) => { const next = [...contacts]; next[idx].email = e.target.value; setContacts(next); }}
                        placeholder={t("Masukkan email")}
                      />
                      {contactErrors[idx]?.email && <div className="lms-error-msg">{contactErrors[idx].email}</div>}
                    </div>
                    <div className={`lms-form-group ${contactErrors[idx]?.phone ? "has-error" : ""}`}>
                      <label className="lms-label">{t("Phone")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                      <input
                        className="lms-input"
                        maxLength={13}
                        value={contact.phone}
                        onChange={(e) => { const next = [...contacts]; next[idx].phone = e.target.value; setContacts(next); }}
                        placeholder={t("Masukkan nomor telepon")}
                      />
                      {contactErrors[idx]?.phone && <div className="lms-error-msg">{contactErrors[idx].phone}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 4: Package Selection */}
          {step === 4 && (
            <div>
              <div className="t-h2" style={{ marginBottom: 20 }}>{t("Package Selection")}</div>
              <div className="lms-step4-grid">
                {/* Left: Package picker */}
                <PackageSelectList
                  packages={packages}
                  packagesLoading={packagesLoading}
                  subscription={subscription}
                  subscriptionErrors={subscriptionErrors}
                  onChange={handleSubscriptionChange}
                />

                {/* Right: Subscription config + price */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                  {/* Subscription period */}
                  <div style={{ border: "1px solid var(--border-subtle)", borderRadius: 14, padding: "16px 18px" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
                      {t("Periode Berlangganan")}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div className={`lms-form-group ${subscriptionErrors.tanggalMulai ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                        <label className="lms-label">{t("Mulai")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                        <input
                          className="lms-input"
                          type="date"
                          value={subscription.tanggalMulai}
                          onChange={(e) => {
                            const newStart = e.target.value;
                            const updates = { tanggalMulai: newStart };
                            if (subscription.tanggalBerakhir && newStart >= subscription.tanggalBerakhir) {
                              updates.tanggalBerakhir = "";
                            }
                            setSubscription({ ...subscription, ...updates });
                          }}
                        />
                        {subscriptionErrors.tanggalMulai && <div className="lms-error-msg">{subscriptionErrors.tanggalMulai}</div>}
                      </div>
                      <div className={`lms-form-group ${subscriptionErrors.tanggalBerakhir ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                        <label className="lms-label">{t("Berakhir")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                        <input
                          className="lms-input"
                          type="date"
                          value={subscription.tanggalBerakhir}
                          min={subscription.tanggalMulai || undefined}
                          onChange={(e) => setSubscription({ ...subscription, tanggalBerakhir: e.target.value })}
                        />
                        {subscriptionErrors.tanggalBerakhir && <div className="lms-error-msg">{subscriptionErrors.tanggalBerakhir}</div>}
                      </div>
                    </div>
                  </div>

                  {/* Pricing options */}
                  <div style={{ border: "1px solid var(--border-subtle)", borderRadius: 14, padding: "16px 18px" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
                      {t("Opsi Harga")}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div className={`lms-form-group ${subscriptionErrors.jumlahStudent ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                        <label className="lms-label">{t("Jumlah Student")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                        <input
                          className="lms-input"
                          type="number"
                          min={1}
                          value={subscription.jumlahStudent}
                          onChange={(e) => setSubscription({ ...subscription, jumlahStudent: Math.max(parseInt(e.target.value) || 1, 1) })}
                        />
                        {subscriptionErrors.jumlahStudent && <div className="lms-error-msg">{subscriptionErrors.jumlahStudent}</div>}
                      </div>
                      <div className={`lms-form-group ${subscriptionErrors.discountPercent ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                        <label className="lms-label">{t("Diskon (%)")}</label>
                        <input
                          className="lms-input"
                          type="number"
                          min={0}
                          max={100}
                          value={subscription.discountPercent}
                          onChange={(e) => {
                            const num = Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100);
                            setSubscription({ ...subscription, discountPercent: num });
                          }}
                        />
                        {subscriptionErrors.discountPercent && <div className="lms-error-msg">{subscriptionErrors.discountPercent}</div>}
                      </div>
                      <div className="lms-form-group" style={{ marginBottom: 0 }}>
                        <label className="lms-label">{t("Biaya Instalasi (Rp)")}</label>
                        <input
                          className="lms-input"
                          type="number"
                          min={0}
                          value={subscription.installationFee}
                          onChange={(e) => setSubscription({ ...subscription, installationFee: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div className={`lms-form-group ${subscriptionErrors.gracePeriod ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                        <label className="lms-label">{t("Grace Period (hari)")}</label>
                        <input
                          className="lms-input"
                          type="number"
                          min={0}
                          value={subscription.gracePeriod}
                          onChange={(e) => setSubscription({ ...subscription, gracePeriod: parseInt(e.target.value) || 0 })}
                        />
                        {subscriptionErrors.gracePeriod && <div className="lms-error-msg">{subscriptionErrors.gracePeriod}</div>}
                      </div>
                    </div>
                  </div>

                  {/* File attachment */}
                  <div style={{ border: "1px solid var(--border-subtle)", borderRadius: 14, padding: "16px 18px" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
                      {t("Lampiran")} <span style={{ fontWeight: 400, textTransform: "none", fontSize: 11 }}>{t("(opsional)")}</span>
                    </div>
                    <label
                      style={{
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        gap: 8, border: "1.5px dashed var(--border-strong)", borderRadius: 10,
                        padding: "20px 16px", cursor: "pointer", background: "var(--slate-50)",
                        transition: "border-color 0.15s, background 0.15s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand-400)"; e.currentTarget.style.background = "var(--brand-50)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-strong)"; e.currentTarget.style.background = "var(--slate-50)"; }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28" style={{ color: "var(--text-muted)" }}>
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      {subscription.additionalAttachment ? (
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--success-700)" }}>{subscription.additionalAttachment.name}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{t("Klik untuk ganti file")}</div>
                        </div>
                      ) : (
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-base)" }}>{t("Klik untuk upload file")}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{t("PDF, DOC, DOCX, atau gambar")}</div>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => setSubscription({ ...subscription, additionalAttachment: e.target.files[0] })}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>

                  {/* Price Summary */}
                  <div className="lms-price-summary">
                    <div className="lms-price-summary-title">{t("Ringkasan Harga")}</div>
                    <div className="lms-price-row">
                      <span>{t("Harga Paket")}</span>
                      <span>Rp {basePrice.toLocaleString("id-ID")} / bulan</span>
                    </div>
                    <div className="lms-price-row">
                      <span>{t("Jumlah Student")}</span>
                      <span>{jumlahStudent} student</span>
                    </div>
                    <div className="lms-price-row">
                      <span>{t("Durasi")}</span>
                      <span>{durationMonths} bulan</span>
                    </div>
                    {subscription.discountPercent > 0 && (
                      <div className="lms-price-row">
                        <span>{t("Diskon")} ({subscription.discountPercent}%)</span>
                        <span style={{ color: "var(--danger-500)" }}>-Rp {(basePrice * jumlahStudent * durationMonths - discountedPrice).toLocaleString("id-ID")}</span>
                      </div>
                    )}
                    <div className="lms-price-row">
                      <span>{t("Biaya Instalasi")}</span>
                      <span>Rp {(subscription.installationFee || 0).toLocaleString("id-ID")}</span>
                    </div>
                    <div className="lms-price-divider" />
                    <div className="lms-price-row lms-price-total">
                      <span>{t("Total")}</span>
                      <span>Rp {totalPrice.toLocaleString("id-ID")}</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}

          {/* Step 5: Default User */}
          {step === 5 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>

              {/* Left: Info panel */}
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--brand-50)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--brand-500)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="t-h2">{t("Default User LMS")}</div>
                    <div className="t-small" style={{ color: "var(--text-muted)", marginTop: 2 }}>{t("Akun login untuk aplikasi LMS Edunara")}</div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, background: "var(--warning-50)", border: "1px solid var(--warning-200)", borderRadius: 12, padding: "12px 14px" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--warning-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" style={{ flexShrink: 0, marginTop: 1 }}>
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <div className="t-small" style={{ color: "var(--warning-800)" }}>
                    {t("Akun ini digunakan untuk login ke aplikasi LMS Edunara milik tenant. Pastikan email dan password valid sebelum melanjutkan.")}
                  </div>
                </div>

                {/* Tips */}
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    { icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z", text: t("Gunakan email yang aktif dan mudah diingat") },
                    { icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z", text: t("Password minimal 6 karakter, kombinasikan huruf dan angka") },
                    { icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z", text: t("Akun ini dapat diubah setelah LMS aktif") },
                  ].map((tip, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--slate-100)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                          <path d={tip.icon}/>
                        </svg>
                      </div>
                      <div className="t-small" style={{ color: "var(--text-base)", paddingTop: 5 }}>{tip.text}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Form */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Email */}
                <div className={`lms-form-group ${defaultUserErrors.email ? "has-error" : ""}`}>
                  <label className="lms-label">{t("Email")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", display: "flex", pointerEvents: "none" }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </span>
                    <input
                      className="lms-input"
                      type="email"
                      value={defaultUser.email}
                      onChange={(e) => {
                        setDefaultUser({ ...defaultUser, email: e.target.value });
                        if (defaultUserErrors.email) setDefaultUserErrors({ ...defaultUserErrors, email: "" });
                      }}
                      placeholder={t("admin@example.com")}
                      style={{ paddingLeft: 38 }}
                    />
                  </div>
                  {defaultUserErrors.email && <div className="lms-error-msg">{defaultUserErrors.email}</div>}
                </div>

                {/* Password */}
                <div className={`lms-form-group ${defaultUserErrors.password ? "has-error" : ""}`}>
                  <label className="lms-label">{t("Password")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", display: "flex", pointerEvents: "none" }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0110 0v4"/>
                      </svg>
                    </span>
                    <input
                      className="lms-input"
                      type={showPassword ? "text" : "password"}
                      value={defaultUser.password}
                      onChange={(e) => {
                        setDefaultUser({ ...defaultUser, password: e.target.value });
                        if (defaultUserErrors.password) setDefaultUserErrors({ ...defaultUserErrors, password: "" });
                      }}
                      placeholder={t("Minimal 6 karakter")}
                      style={{ paddingLeft: 38, paddingRight: 44 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", padding: 0 }}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  {defaultUserErrors.password && <div className="lms-error-msg">{defaultUserErrors.password}</div>}
                </div>

                {/* Password strength */}
                {defaultUser.password.length > 0 && (() => {
                  const len = defaultUser.password.length;
                  const strength = len < 6 ? 1 : len < 10 ? 2 : 3;
                  const colors = ["", "var(--danger-500)", "var(--warning-500)", "var(--success-500)"];
                  const labels = ["", t("Lemah"), t("Sedang"), t("Kuat")];
                  return (
                    <div style={{ marginTop: -8 }}>
                      <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                        {[1,2,3].map((i) => (
                          <div key={i} style={{ flex: 1, height: 3, borderRadius: 99, background: i <= strength ? colors[strength] : "var(--border-subtle)", transition: "background 0.2s" }} />
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: colors[strength], fontWeight: 500 }}>{labels[strength]}</div>
                    </div>
                  );
                })()}
              </div>

            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20 }}>
        <button
          className="lms-btn lms-btn-secondary"
          onClick={back}
          disabled={step === 1}
        >
          {t("Previous")}
        </button>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="lms-btn lms-btn-ghost" onClick={() => history.goBack()}>
            {t("Cancel")}
          </button>
          {step < 5 ? (
            <button className="lms-btn lms-btn-primary" onClick={next}>
              {t("Next")}
            </button>
          ) : (
            <button
              className="lms-btn lms-btn-primary"
              onClick={save}
              disabled={loading}
            >
              {loading ? <span className="lms-spinner" style={{ width: 14, height: 14 }} /> : <SaveIcon />}
              {loading ? t("Creating...") : (mode === "edit" ? t("Save Changes") : t("Create Tenant"))}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TenantCreateStep;
